import { useState, useCallback } from 'react';
import { LeadsFilters } from '../../../entities/lead/model/api';
import type { DocType } from '../../../entities/lead/model/types';

export interface FilterState {
  userIds: string[];
  groupIds: string[];
  city: string;
  boardIds: string[];
  createdFrom: string;
  createdTo: string;
  updatedFrom: string;
  updatedTo: string;
  noJobs: boolean;
  tagIds: string[];
  sources: string[];
  docTypes: DocType[];
}

const STORAGE_KEY = 'kanban_filters';

const emptyFilter: FilterState = {
  userIds: [],
  groupIds: [],
  city: '',
  boardIds: [],
  createdFrom: '',
  createdTo: '',
  updatedFrom: '',
  updatedTo: '',
  noJobs: false,
  tagIds: [],
  sources: [],
  docTypes: [],
};

function loadFromStorage(): FilterState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyFilter;
    const parsed = JSON.parse(raw) as Partial<FilterState>;
    return {
      userIds: Array.isArray(parsed.userIds) ? parsed.userIds : [],
      groupIds: Array.isArray(parsed.groupIds) ? parsed.groupIds : [],
      city: parsed.city ?? '',
      boardIds: Array.isArray(parsed.boardIds) ? parsed.boardIds : [],
      createdFrom: parsed.createdFrom ?? '',
      createdTo: parsed.createdTo ?? '',
      updatedFrom: parsed.updatedFrom ?? '',
      updatedTo: parsed.updatedTo ?? '',
      noJobs: parsed.noJobs ?? false,
      tagIds: Array.isArray(parsed.tagIds) ? parsed.tagIds : [],
      sources: Array.isArray(parsed.sources) ? parsed.sources : [],
      docTypes: Array.isArray(parsed.docTypes) ? parsed.docTypes : [],
    };
  } catch {
    return emptyFilter;
  }
}

function isActive(f: FilterState): boolean {
  return Boolean(f.userIds.length || f.groupIds.length || f.city || f.boardIds.length || f.createdFrom || f.createdTo || f.updatedFrom || f.updatedTo || f.noJobs || f.tagIds.length || f.sources.length || f.docTypes.length);
}

export function toLeadsFilters(f: FilterState): LeadsFilters {
  const result: LeadsFilters = {};
  if (f.userIds.length) result.userIds = f.userIds;
  if (f.city) result.city = f.city;
  if (f.boardIds.length) result.boardIds = f.boardIds;
  if (f.createdFrom) result.createdFrom = f.createdFrom;
  if (f.createdTo) result.createdTo = f.createdTo;
  if (f.updatedFrom) result.updatedFrom = f.updatedFrom;
  if (f.updatedTo) result.updatedTo = f.updatedTo;
  if (f.noJobs) result.noJobs = true;
  if (f.tagIds.length) result.tagIds = f.tagIds;
  if (f.sources.length) result.sources = f.sources;
  if (f.docTypes?.length) result.types = f.docTypes;
  return result;
}

export function getStoredLeadsFilters(): LeadsFilters {
  return toLeadsFilters(loadFromStorage());
}

export function useFilterStore() {
  const [filters, setFilters] = useState<FilterState>(loadFromStorage);
  const [isOpen, setIsOpen] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(() => isActive(loadFromStorage()));

  const toggleOpen = useCallback(() => setIsOpen(prev => !prev), []);
  const close = useCallback(() => setIsOpen(false), []);

  const applyFilters = useCallback((f: FilterState) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(f));
    setFilters(f);
    setHasActiveFilters(isActive(f));
    setIsOpen(false);
    return toLeadsFilters(f);
  }, []);

  const resetFilters = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setFilters(emptyFilter);
    setHasActiveFilters(false);
    return toLeadsFilters(emptyFilter);
  }, []);

  return { filters, isOpen, hasActiveFilters, toggleOpen, close, applyFilters, resetFilters };
}
